// Single source of truth for AppShell navigation.
// Used by SideNav (desktop) and BottomNav (mobile).
// Rules (locked):
//   • Normal user / beta tester mobile: 5 tabs only.
//   • Founder/admin mobile: 5 tabs + Founder shortcut (optional).
//   • Desktop: extra tiles (Style Pros, Shop, Founder Console) appear once.
//   • Style Pros visible only when `style_services_v1` flag is on for the
//     user OR the viewer is admin/founder (admin preview).
//   • Shop visible only when commerce flag is on for the user OR admin.

const CORE_SECTIONS = [
  { key: 'home',    label: 'Home',    path: '/home',    icon: 'home' },
  { key: 'stylist', label: 'Stylist', path: '/stylist', icon: 'stylist' },
  { key: 'closet',  label: 'Closet',  path: '/closet',  icon: 'closet' },
  { key: 'social',  label: 'Social',  path: '/social',  icon: 'social' },
  { key: 'profile', label: 'Profile', path: '/profile', icon: 'profile' },
];

const CONSOLE_BLOCKED_ACCOUNT_STATES = new Set([
  'paused', 'restricted', 'suspended', 'revoked', 'banned', 'deactivated',
  'deleted', 'disabled', 'closed', 'anonymized', 'deletion_in_progress',
]);

export const userCanAccessFounderConsole = (user) => {
  if (!user) return false;
  const capabilities = Array.isArray(user.capabilities) ? user.capabilities : [];
  const serverAllows = user.canAccessFounderConsole === true
    || user.can_access_console === true
    || user.is_admin === true // compatibility during the capability rollout
    || capabilities.includes('*')
    || capabilities.includes('console.access');
  if (!serverAllows) return false;
  // `/api/auth/me` owns the capability decision. Never reconstruct it from a
  // role or email on the client. The explicit state check is defense in depth
  // for a stale cached identity during an account suspension/revocation.
  const accountState = String(user.account_status || user.status || 'active')
    .trim().toLowerCase();
  return !CONSOLE_BLOCKED_ACCOUNT_STATES.has(accountState);
};

// Compatibility name used by FounderShell while the Founder Studio shell is
// redesigned. It intentionally means console access, not generic role truth.
export const userIsAdmin = userCanAccessFounderConsole;

const flagOn = (user, name) =>
  !!(user?.flags?.[name]?.enabled);

// Build the list of nav items exactly once per call. Returns the deduplicated
// list — the helper is the only place that decides item visibility, so the
// caller cannot accidentally double-push the same key.
export const buildNavItems = ({ user, viewport = 'mobile' }) => {
  const isAdmin = userIsAdmin(user);
  const hasServices = flagOn(user, 'style_services_v1') || isAdmin;
  const hasShop = flagOn(user, 'shop_pages_v1') || isAdmin;

  const items = [...CORE_SECTIONS];

  if (viewport === 'desktop') {
    if (hasShop) items.push({ key: 'shop', label: 'Shop', path: '/shop', icon: 'shop' });
    if (hasServices) items.push({ key: 'services', label: 'Style Pros', path: '/marketplace', icon: 'services' });
    if (isAdmin) items.push({ key: 'admin', label: 'Founder Console', path: '/founder', icon: 'admin' });
  } else {
    // mobile: only the founder shortcut (optional) — never Style Pros / Shop / Camera tabs.
    if (isAdmin) items.push({ key: 'admin', label: 'Founder', path: '/founder', icon: 'admin' });
  }

  // Final dedup safeguard (defensive — should never fire after the gates above).
  const seen = new Set();
  return items.filter((it) => {
    if (seen.has(it.key)) return false;
    seen.add(it.key);
    return true;
  });
};

